import { useState } from "react";
import { HubAdminDemoLayout } from "@/layout/HubAdminDemoLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
} from "recharts";
import {
  MOCK_CANDIDATES,
  compositeScore,
  categoryAvg,
  candidateDisagreementScore,
  CRITERIA_GROUPS,
  CATEGORY_NAMES,
  MOCK_EVALUATORS,
  MOCK_SCORES,
} from "@/components/hub/mockSelectionData";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TODAY = new Date().toLocaleDateString("en-US", {
  month: "long", day: "numeric", year: "numeric",
});

const AGE_GROUPS = ["U11", "U13", "U15", "U17"] as const;
type AgeGroup = (typeof AGE_GROUPS)[number];

const STATUS_BY_ID: Record<number, "Selected" | "Reserve" | "Not Selected"> = {
  1: "Selected", 2: "Selected", 3: "Reserve", 4: "Selected",
  5: "Reserve",  6: "Not Selected", 7: "Not Selected", 8: "Not Selected",
  9: "Selected", 10: "Reserve", 11: "Selected", 12: "Reserve",
  13: "Selected", 14: "Reserve",
};

const STATUS_BADGE_CLS: Record<string, string> = {
  "Selected":     "bg-green-100 text-green-800 border-green-300",
  "Reserve":      "bg-amber-100 text-amber-800 border-amber-300",
  "Not Selected": "bg-red-100 text-red-700 border-red-300",
};

function avgCatScore(candidateId: number, category: string): number {
  const vals = MOCK_EVALUATORS.map((ev) => categoryAvg(candidateId, ev, category));
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function criterionAvg(candidateId: number, key: string): number {
  const vals = MOCK_EVALUATORS.map((ev) => MOCK_SCORES[candidateId][ev][key]);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function buildRationale(candidateId: number, status: string): string {
  const catAvgs = CATEGORY_NAMES.map((c) => ({ c, v: avgCatScore(candidateId, c) }))
    .sort((a, b) => b.v - a.v);
  const top = catAvgs[0].c;
  const bottom = catAvgs[catAvgs.length - 1].c;
  const score = compositeScore(candidateId).toFixed(2);
  if (status === "Selected")
    return `Selected based on consistently strong performance across ${top}. Composite score of ${score}.`;
  if (status === "Reserve")
    return `Placed in reserve based on ${top} strength. Areas to develop before next phase: ${bottom}.`;
  return `Not selected for this phase. Demonstrated strength in ${top}. Focus areas: ${bottom}. We encourage continued participation.`;
}

const CAT_COLORS: Record<string, string> = {
  "Core Skills":     "bg-blue-500",
  "Athleticism":     "bg-green-500",
  "Match Awareness": "bg-purple-500",
  "Temperament":     "bg-amber-500",
};

const CANDIDATE_COLORS = ["#3b82f6", "#10b981"];

// ---------------------------------------------------------------------------
// Printable overlay components
// ---------------------------------------------------------------------------

interface BatchOverlayProps { ageGroup: AgeGroup; onClose: () => void }

function BatchScorecardOverlay({ ageGroup, onClose }: BatchOverlayProps) {
  const candidates = MOCK_CANDIDATES
    .filter((c) => c.ageGroup === ageGroup)
    .map((c) => ({ ...c, composite: compositeScore(c.id), status: STATUS_BY_ID[c.id] ?? "Not Selected" }))
    .sort((a, b) => b.composite - a.composite);

  return (
    <div className="printable-overlay fixed inset-0 z-50 bg-white overflow-y-auto" data-testid="batch-overlay">
      {/* Fixed controls */}
      <div className="fixed top-4 right-4 flex gap-2 z-50 print:hidden">
        <Button size="sm" variant="outline" onClick={() => window.print()}>Print / Save PDF</Button>
        <Button size="sm" onClick={onClose}>Close</Button>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-12 space-y-12">
        {/* Cover */}
        <div className="text-center space-y-3 py-12 border-b border-gray-200">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Dallas Hub</p>
          <h1 className="text-3xl font-bold text-gray-900">{ageGroup} Selection Report</h1>
          <p className="text-gray-500">Phase 2 Spring 2026 · Generated {TODAY}</p>
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">DEMO — Sample data</Badge>
        </div>

        {/* Rankings table */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-3">Rankings</h2>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3 text-left">Rank</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Position</th>
                  <th className="px-4 py-3 text-center">Composite</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {candidates.map((c, i) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{c.name}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{c.position}</td>
                    <td className="px-4 py-3 text-center font-bold text-gray-800">{c.composite.toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge className={`border text-xs ${STATUS_BADGE_CLS[c.status]}`}>{c.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Per-kid scorecards */}
        {candidates.map((c) => {
          const catAvgs = CATEGORY_NAMES.map((cat) => ({
            cat, avg: avgCatScore(c.id, cat),
          })).sort((a, b) => b.avg - a.avg);

          return (
            <div key={c.id} className="border border-gray-200 rounded-xl p-6 space-y-4 break-inside-avoid">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-lg font-bold text-gray-900">{c.name}</p>
                  <p className="text-sm text-gray-500">{c.ageGroup} · {c.position}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-800">{c.composite.toFixed(2)}</p>
                  <Badge className={`border text-xs mt-1 ${STATUS_BADGE_CLS[c.status]}`}>{c.status}</Badge>
                </div>
              </div>

              {/* Category progress bars */}
              <div className="space-y-2">
                {catAvgs.map(({ cat, avg }) => (
                  <div key={cat}>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-gray-600">{cat}</span>
                      <span className="text-xs font-semibold text-gray-800">{avg.toFixed(2)}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${CAT_COLORS[cat] ?? "bg-gray-400"}`}
                        style={{ width: `${Math.round((avg / 5) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Parent rationale */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Draft Parent Communication</p>
                <p className="text-sm text-gray-700">{buildRationale(c.id, c.status)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel comparison overlay (hardcoded Ansh Shah ≈ id:1 vs Jordan ≈ id:2)
// Production: pulls most recently saved comparison from session state.
// ---------------------------------------------------------------------------

function PanelComparisonOverlay({ onClose }: { onClose: () => void }) {
  const c1 = MOCK_CANDIDATES[0]; // Alex Chen (id 1)
  const c2 = MOCK_CANDIDATES[1]; // Jordan Patel (id 2)
  const pair = [c1, c2];

  const radarData = CATEGORY_NAMES.map((cat) => ({
    category: cat,
    [c1.name]: parseFloat(avgCatScore(c1.id, cat).toFixed(2)),
    [c2.name]: parseFloat(avgCatScore(c2.id, cat).toFixed(2)),
  }));

  const deltaRows = CATEGORY_NAMES.map((cat) => {
    const s1 = avgCatScore(c1.id, cat);
    const s2 = avgCatScore(c2.id, cat);
    return { cat, s1, s2, delta: Math.abs(s1 - s2) };
  }).sort((a, b) => b.delta - a.delta);

  const biggestGap = deltaRows[0];
  const aiText = `${c1.name} and ${c2.name} diverge most on ${biggestGap.cat} (+${biggestGap.delta.toFixed(1)} gap). Suggested panel discussion: review ${biggestGap.cat} drills from the selection trial.`;

  return (
    <div className="printable-overlay fixed inset-0 z-50 bg-white overflow-y-auto" data-testid="comparison-overlay">
      <div className="fixed top-4 right-4 flex gap-2 z-50 print:hidden">
        <Button size="sm" variant="outline" onClick={() => window.print()}>Print Comparison Report</Button>
        <Button size="sm" onClick={onClose}>Close</Button>
      </div>

      <div className="max-w-3xl mx-auto px-8 py-12 space-y-8">
        <div className="text-center space-y-2 pb-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Panel Comparison Report</h1>
          <p className="text-gray-500">{c1.name} vs {c2.name} · Phase 2 Spring 2026</p>
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">DEMO</Badge>
        </div>

        {/* Radar */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Category Radar</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: "#6b7280" }} />
                {pair.map((c, i) => (
                  <Radar
                    key={c.id}
                    name={c.name}
                    dataKey={c.name}
                    stroke={CANDIDATE_COLORS[i]}
                    fill={CANDIDATE_COLORS[i]}
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                ))}
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Delta table */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Score Breakdown (sorted by gap ↓)</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
                  <th className="px-4 py-2 text-left">Category</th>
                  <th className="px-4 py-2 text-center" style={{ color: CANDIDATE_COLORS[0] }}>{c1.name}</th>
                  <th className="px-4 py-2 text-center" style={{ color: CANDIDATE_COLORS[1] }}>{c2.name}</th>
                  <th className="px-4 py-2 text-center text-gray-400">Gap</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {deltaRows.map(({ cat, s1, s2, delta }) => {
                  const s1High = s1 > s2;
                  return (
                    <tr key={cat} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-700 text-xs font-medium">{cat}</td>
                      <td className={`px-4 py-2 text-center text-xs font-semibold ${s1High ? "text-green-700 bg-green-50" : "text-red-600 bg-red-50"}`}>{s1.toFixed(1)}</td>
                      <td className={`px-4 py-2 text-center text-xs font-semibold ${!s1High ? "text-green-700 bg-green-50" : "text-red-600 bg-red-50"}`}>{s2.toFixed(1)}</td>
                      <td className="px-4 py-2 text-center text-xs text-gray-400">{delta.toFixed(1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* AI insight */}
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
          <p className="text-xs font-semibold text-amber-800 mb-1">Disagreement Triage Agent</p>
          <p className="text-sm text-amber-700">{aiText}</p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evaluation data overlay
// ---------------------------------------------------------------------------

interface EvalOverlayProps { ageGroup: AgeGroup; onClose: () => void }

function EvaluationDataOverlay({ ageGroup, onClose }: EvalOverlayProps) {
  const candidates = MOCK_CANDIDATES
    .filter((c) => c.ageGroup === ageGroup)
    .sort((a, b) => compositeScore(b.id) - compositeScore(a.id));

  return (
    <div className="printable-overlay fixed inset-0 z-50 bg-white overflow-y-auto" data-testid="eval-overlay">
      <div className="fixed top-4 right-4 flex gap-2 z-50 print:hidden">
        <Button size="sm" variant="outline" onClick={() => window.print()}>Print / Save PDF</Button>
        <Button size="sm" onClick={onClose}>Close</Button>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-12 space-y-8">
        <div className="text-center space-y-2 pb-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Evaluation Data — {ageGroup}</h1>
          <p className="text-gray-500">Phase 2 Spring 2026 · Generated {TODAY}</p>
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">DEMO</Badge>
        </div>

        {/* Raw scores table */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Raw Selector Scores by Category</p>
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase">
                  <th className="px-3 py-2 text-left">Candidate</th>
                  <th className="px-3 py-2 text-left">Evaluator</th>
                  {CATEGORY_NAMES.map((cat) => (
                    <th key={cat} className="px-3 py-2 text-center">{cat}</th>
                  ))}
                  <th className="px-3 py-2 text-center">Avg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {candidates.flatMap((c) =>
                  MOCK_EVALUATORS.map((ev, evIdx) => {
                    const catScores = CATEGORY_NAMES.map((cat) => categoryAvg(c.id, ev, cat));
                    const rowAvg = catScores.reduce((a, b) => a + b, 0) / catScores.length;
                    return (
                      <tr key={`${c.id}-${ev}`} className="hover:bg-gray-50">
                        <td className={`px-3 py-2 text-xs font-medium ${evIdx === 0 ? "text-gray-900" : "text-gray-300"}`}>
                          {evIdx === 0 ? c.name : ""}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{ev}</td>
                        {catScores.map((score, i) => (
                          <td key={i} className="px-3 py-2 text-center text-xs text-gray-700">{score.toFixed(1)}</td>
                        ))}
                        <td className="px-3 py-2 text-center text-xs font-bold text-gray-800">{rowAvg.toFixed(2)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Disagreement section */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Selector Disagreement</p>
          <div className="space-y-2">
            {candidates.map((c) => {
              const evAvgs = MOCK_EVALUATORS.map((ev) => {
                const vals = Object.values(MOCK_SCORES[c.id][ev]);
                return vals.reduce((a, b) => a + b, 0) / vals.length;
              });
              const gap = Math.max(...evAvgs) - Math.min(...evAvgs);
              const flagged = gap > 0.8;
              return (
                <div
                  key={c.id}
                  className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${flagged ? "border-amber-300 bg-amber-50" : "border-gray-100 bg-gray-50"}`}
                >
                  <span className={`text-sm font-medium ${flagged ? "text-amber-800" : "text-gray-700"}`}>{c.name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">Gap: {gap.toFixed(2)}</span>
                    {flagged && <span className="text-xs text-amber-700">⚠️ Panel disagreement noted.</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function HubReportsDemo() {
  const { toast } = useToast();
  const [batchAge, setBatchAge] = useState<AgeGroup | null>(null);
  const [showBatch, setShowBatch] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [evalAge, setEvalAge] = useState<AgeGroup | null>(null);
  const [showEval, setShowEval] = useState(false);

  // Derive category averages for the sample report (first candidate)
  const sampleCandidate = MOCK_CANDIDATES[0];
  const sampleComposite = compositeScore(sampleCandidate.id);
  const sampleCatAvgs = CATEGORY_NAMES.map((cat) => ({
    cat, avg: avgCatScore(sampleCandidate.id, cat),
  })).sort((a, b) => b.avg - a.avg);
  const sampleTopCat = sampleCatAvgs[0].cat;
  const sampleBottomCat = sampleCatAvgs[sampleCatAvgs.length - 1].cat;
  const sampleRationale = `Selected based on consistently strong performance across ${sampleTopCat}. Composite score of ${sampleComposite.toFixed(2)} ranked 2nd in the pool. Areas to continue developing: ${sampleBottomCat}.`;

  return (
    <HubAdminDemoLayout>
      {/* Print styles */}
      <style>{`@media print { body > * { display: none } .printable-overlay { display: block !important } }`}</style>

      <div className="px-4 md:px-8 py-6 space-y-6" data-testid="hub-reports-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">Reports</h1>
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-xs">DEMO</Badge>
        </div>

        {/* Report cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="report-cards-grid">

          {/* 1 — Batch Scorecard */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="report-card-selection">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📋</span>
              <div>
                <p className="font-semibold text-gray-900">Selection Report</p>
                <p className="text-sm text-gray-500 mt-0.5">Per-candidate scorecard with rationale</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="batch-age-pills">
              {AGE_GROUPS.map((ag) => (
                <button
                  key={ag}
                  onClick={() => setBatchAge(ag)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    batchAge === ag
                      ? "bg-[#1E2761] text-white border-[#1E2761]"
                      : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                  }`}
                  data-testid={`batch-pill-${ag.toLowerCase()}`}
                >
                  {ag}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              disabled={!batchAge}
              onClick={() => setShowBatch(true)}
              data-testid="btn-generate-selection-report"
            >
              Export Batch Report
            </Button>
          </div>

          {/* 2 — Panel Comparison */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="report-card-tournament">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚖️</span>
              <div>
                <p className="font-semibold text-gray-900">Panel Comparison</p>
                <p className="text-sm text-gray-500 mt-0.5">Side-by-side scoring with radar chart</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowComparison(true)}
              data-testid="btn-open-panel-comparison"
            >
              Print Comparison Report
            </Button>
          </div>

          {/* 3 — Payment Report (stub) */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="report-card-payment">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💳</span>
              <div>
                <p className="font-semibold text-gray-900">Payment Report</p>
                <p className="text-sm text-gray-500 mt-0.5">Outstanding payments by academy</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => toast({ title: "Coming in Phase 2", description: "CSV export will be available in Phase 2." })} data-testid="btn-download-payment-csv">
              Download CSV
            </Button>
          </div>

          {/* 4 — Evaluation Data */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="report-card-academy">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📊</span>
              <div>
                <p className="font-semibold text-gray-900">Evaluation Data</p>
                <p className="text-sm text-gray-500 mt-0.5">Raw selector scores + disagreement flags</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="eval-age-pills">
              {AGE_GROUPS.map((ag) => (
                <button
                  key={ag}
                  onClick={() => setEvalAge(ag)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    evalAge === ag
                      ? "bg-[#1E2761] text-white border-[#1E2761]"
                      : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                  }`}
                  data-testid={`eval-pill-${ag.toLowerCase()}`}
                >
                  {ag}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              disabled={!evalAge}
              onClick={() => setShowEval(true)}
              data-testid="btn-view-academy-report"
            >
              View Evaluation Data
            </Button>
          </div>
        </div>

        {/* Sample Selection Report */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="sample-selection-report">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sample — Selection Report</p>
          </div>
          <div className="px-5 py-5 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-lg font-bold text-gray-900">{sampleCandidate.name}</p>
                <p className="text-sm text-gray-500">{sampleCandidate.ageGroup} · {sampleCandidate.position}</p>
              </div>
              <Badge className="bg-green-100 text-green-800 border-green-300 border text-xs shrink-0 mt-1">Selected</Badge>
            </div>
            <div className="flex items-center gap-4 py-3 border-y border-gray-100">
              <div className="text-3xl font-bold text-gray-800">{sampleComposite.toFixed(2)}</div>
              <p className="text-sm text-gray-500">Composite score<br /><span className="text-xs text-gray-400">avg across 23 criteria · 3 selectors</span></p>
            </div>
            <div className="space-y-3">
              {sampleCatAvgs.map(({ cat, avg }) => (
                <div key={cat}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">{cat}</span>
                    <span className="text-sm font-semibold text-gray-800">{avg.toFixed(2)}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${CAT_COLORS[cat] ?? "bg-gray-400"}`} style={{ width: `${Math.round((avg / 5) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Draft Parent Communication</p>
              <p className="text-sm text-gray-700 leading-relaxed">{sampleRationale}</p>
            </div>
          </div>
        </div>

        {/* AI banner */}
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-5 py-4" data-testid="ai-narrative-banner">
          <p className="text-sm font-semibold text-amber-800 mb-1">AI Narrative Generation</p>
          <p className="text-sm text-amber-700 leading-relaxed">
            In Phase 2, the Cross-Stage Narrative Agent will auto-draft this rationale from scoring data. Human review required before sending.
          </p>
        </div>
      </div>

      {/* Printable overlays */}
      {showBatch && batchAge && <BatchScorecardOverlay ageGroup={batchAge} onClose={() => setShowBatch(false)} />}
      {showComparison && <PanelComparisonOverlay onClose={() => setShowComparison(false)} />}
      {showEval && evalAge && <EvaluationDataOverlay ageGroup={evalAge} onClose={() => setShowEval(false)} />}
    </HubAdminDemoLayout>
  );
}
